import type { PortraitStudent } from '@/api/types/portrait'
import { studentKeyOf } from './helpers/student-key'

/**
 * Whether one student counts as placed right now: their portrait rows with the session's
 * placements laid over the server's per-portrait `usedOnPage`.
 */
function isStudentPlacedNow(
  rows: PortraitStudent[],
  overrides: ReadonlyMap<string, boolean>
): boolean {
  if (rows.some((row) => overrides.get(row.portraitId) ?? row.usedOnPage === true)) {
    return true
  }

  // Nothing is placed now. If the server counted a portrait of theirs, this session has since
  // removed every one of those (an unremoved one would still read as placed above), so the
  // student is no longer placed.
  if (rows.some((row) => row.usedOnPage === true)) {
    return false
  }

  // The server's own reason, if it has one, is not among these rows (a portrait the current
  // filters hide, such as a secondary one under a Grade), so its flag stands.
  return rows.some((row) => row.studentUsedOnPage === true)
}

/**
 * Lays this session's own portrait placements over the roster, as `applyClikArtPlacementOverrides`
 * does for ClikArt: the server's flag is only as fresh as its last indexing, so a portrait placed
 * or removed on a spread in this session must show at once. `overrides` is the photo placement
 * session (`imageId` -> placed): a placed portrait is a photo fill, and its image id is the
 * roster's `portraitId`. A portrait the session never touched has no entry and keeps the server's
 * value, so with no matching ids the roster comes back untouched, rows and all.
 *
 * The student-level flag is recomputed from the student's rows, since it is true when ANY of their
 * portraits is placed (`usedOnPage` is only this row's).
 */
export function applyPortraitPlacementOverrides(
  students: PortraitStudent[],
  overrides: ReadonlyMap<string, boolean>
): PortraitStudent[] {
  if (overrides.size === 0) {
    return students
  }

  const rowsByStudent = new Map<string, PortraitStudent[]>()

  for (const student of students) {
    const key = studentKeyOf(student)
    const rows = rowsByStudent.get(key)

    if (rows) {
      rows.push(student)
    } else {
      rowsByStudent.set(key, [student])
    }
  }

  const placedByStudent = new Map<string, boolean>()

  for (const [key, rows] of rowsByStudent) {
    placedByStudent.set(key, isStudentPlacedNow(rows, overrides))
  }

  let changed = false
  const next = students.map((student) => {
    const usedOnPage = overrides.get(student.portraitId) ?? student.usedOnPage
    const studentUsedOnPage = placedByStudent.get(studentKeyOf(student))

    if (usedOnPage === student.usedOnPage && studentUsedOnPage === student.studentUsedOnPage) {
      return student
    }

    changed = true

    return { ...student, usedOnPage, studentUsedOnPage }
  })

  return changed ? next : students
}

/**
 * The student-level placed flag worked out from the rows themselves: a student is placed when any
 * row of theirs has `usedOnPage`. This is the fallback for a roster fetched without the server's
 * `studentUsedOnPage`, and it is only as complete as the rows it is given: a portrait the request
 * left out (a secondary one under a Grade) cannot count.
 */
export function deriveStudentPlacement(students: PortraitStudent[]): PortraitStudent[] {
  const placedStudents = new Set<string>()

  for (const student of students) {
    if (student.usedOnPage === true) {
      placedStudents.add(studentKeyOf(student))
    }
  }

  return students.map((student) => ({
    ...student,
    studentUsedOnPage: placedStudents.has(studentKeyOf(student)),
  }))
}
